import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import ModuleCard from "@/components/ModuleCard";
import VibrantKpi from "@/components/VibrantKpi";
import ComunicadosAlerts from "@/components/ComunicadosAlerts";
import DashboardAlertsCenter from "@/components/DashboardAlertsCenter";
import PlacspCacheHealthWidget from "@/components/PlacspCacheHealthWidget";
import OportunidadesCpvWidget from "@/components/OportunidadesCpvWidget";
import DashboardCashflowChart from "@/components/DashboardCashflowChart";
import DashboardMiniCharts from "@/components/DashboardMiniCharts";
import SmartInsights from "@/components/SmartInsights";
import GreetingHeader from "@/components/GreetingHeader";
import OnboardingBanner from "@/components/OnboardingBanner";
import FiscalConcentracionBanner from "@/components/FiscalConcentracionBanner";
import PresentationMode from "@/components/PresentationMode";
import { MODULES, CATEGORIES, modulesByCategory } from "@/lib/modules";
import MiDiaPanel from "@/components/MiDiaPanel";
import api, { formatCurrency } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LayoutGrid, Rows3, Shield, Receipt, FileSearch, BadgeDollarSign, Wallet, HardHat } from "lucide-react";

const LAYOUT_KEY = "legatum.dashboard.layout";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [deltas, setDeltas] = useState(null);
  const [presentMode, setPresentMode] = useState(() => searchParams.get("present") === "auto");
  const [layout, setLayout] = useState(() => {
    try { return localStorage.getItem(LAYOUT_KEY) || "bento"; } catch { return "bento"; }
  });

  // Mantener el flag URL sincronizado cuando se sale manualmente del modo
  const closePresent = () => {
    setPresentMode(false);
    if (searchParams.get("present")) {
      searchParams.delete("present");
      setSearchParams(searchParams, { replace: true });
    }
  };

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/dashboard/kpi_deltas").then((r) => setDeltas(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    try { localStorage.setItem(LAYOUT_KEY, layout); } catch (_e) { /* ignore storage errors */ }
  }, [layout]);

  const allowedMods = user?.modules_allowed;
  // Admin ve TODOS los módulos siempre (no se filtra por modules_allowed legacy)
  const isAdminRole = user?.role === "admin";
  const visibleModules = MODULES.filter((m) => {
    // Iter 147 · ocultar módulos deprecados (fusionados en otros, p.ej. Jurídico → LEGAL)
    if (m.deprecated || m.category === "_hidden") return false;
    return isAdminRole || !Array.isArray(allowedMods) || allowedMods.length === 0 || allowedMods.includes(m.key);
  });

  const statForModule = (key) => {    if (!stats) return null;
    switch (key) {
      case "avales":
      case "avales_garantias":
        return { label: "Avales vigentes", value: formatCurrency(stats.avales.total_importe) };
      case "biblioteca":
        return { label: "Documentos", value: stats.documentos };
      case "ia":
        return { label: "Análisis IA", value: stats.analisis_ia };
      case "obra":
        return { label: "Obras en curso", value: `${stats.obras.en_curso} / ${stats.obras.total}` };
      case "facturacion":
        return { label: "Facturado", value: formatCurrency(stats.facturas?.facturado || 0) };
      case "licitaciones":
        return { label: "Activas", value: stats.licitaciones?.activas ?? 0 };
      case "garantias":
        return { label: "Pendiente", value: formatCurrency(stats.garantias.pendiente) };
      case "compras":
      case "adquisiciones":
        return { label: "Pendientes", value: stats.compras.pendientes };
      case "clientes":
        return { label: "Clientes", value: stats.clientes ?? 0 };
      default:
        return null;
    }
  };

  return (
    <>
      {presentMode && <PresentationMode onClose={closePresent} />}
      <Layout>
        <GreetingHeader />
        <OnboardingBanner />

        {/* Iter 130.ae · Banner de modo concentración fiscal (sticky top) */}
        <FiscalConcentracionBanner />

        {/* Iter 130.k · Banner de plazos legales urgentes */}
        <LegalDeadlineBanner />

        {/* Insights inteligentes — alertas derivadas de deltas/umbrales */}
        <SmartInsights />

        {/* Centro de alertas — PRIMERO arriba del todo */}
        <DashboardAlertsCenter />

      {/* Iter 165 · Salud del cache PLACSP (solo admin/gestor) */}
      <div className="mb-3">
        <PlacspCacheHealthWidget />
      </div>

      {/* Iter 166f · Oportunidades de hoy por familia CPV */}
      <div className="mb-3">
        <OportunidadesCpvWidget />
      </div>

      {/* Iter 98 · Mi día — agregado de pendientes operativas del usuario */}
      <MiDiaPanel />

      {/* KPI row — vibrant gradient cards (clicables: saltan al módulo prefiltrado) */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--legatum-text-muted)] font-bold">
          Resumen económico
        </div>
        <PresentationMode.Trigger onClick={() => setPresentMode(true)} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mb-6 md:mb-8" data-testid="dashboard-kpis">
        <VibrantKpi label="Avales vigentes" value={stats ? formatCurrency(stats.avales.total_importe) : "—"} color="#0F8A5F" Icon={Shield} delay={0.00} testId="kpi-avales" delta={deltas?.avales} onClick={() => navigate("/avales?kpi=avales-vigentes")} />
        <VibrantKpi label="Total facturado" value={stats ? formatCurrency(stats.facturas?.facturado || 0) : "—"} color="#127372" Icon={Receipt} delay={0.05} testId="kpi-facturado" delta={deltas?.facturado} onClick={() => navigate("/facturacion?kpi=total-facturado")} />
        <VibrantKpi label="Pdte. facturar"  value={stats ? formatCurrency(stats.facturas?.pendiente_facturar || 0) : "—"} color="#D85A1F" Icon={FileSearch} delay={0.10} testId="kpi-pendiente-facturar" delta={deltas?.pdte_facturar} goodWhen="down" onClick={() => navigate("/facturacion")} />
        <VibrantKpi label="Por cobrar"      value={stats ? formatCurrency(stats.facturas?.por_cobrar || 0) : "—"} color="#C28A2A" Icon={Wallet} delay={0.15} testId="kpi-por-cobrar" delta={deltas?.por_cobrar} goodWhen="down" onClick={() => navigate("/facturacion?kpi=por-cobrar")} />
        <VibrantKpi label="Gtía. a recuperar" value={stats ? formatCurrency(stats.garantias.pendiente) : "—"} color="#6D43B8" Icon={BadgeDollarSign} delay={0.20} testId="kpi-garantia" delta={deltas?.garantia} goodWhen="down" onClick={() => navigate("/garantias?kpi=pendiente-recuperar")} />
        <VibrantKpi label="Obras en curso"  value={stats ? `${stats.obras.en_curso}` : "—"} color="#E08A1F" Icon={HardHat} delay={0.25} subtitle={stats ? `de ${stats.obras.total} totales` : ""} testId="kpi-obras" delta={deltas?.obras} onClick={() => navigate("/obras?kpi=obras-en-curso")} />
      </div>

      {/* Alertas de comunicados internos */}
      <ComunicadosAlerts />

      {/* Pulso financiero global — gráfica cobros vs pagos próximos meses */}
      <DashboardCashflowChart />

      {/* Mini-gráficas: obras por estado · tareas por trabajador · facturación */}
      <DashboardMiniCharts />

      {/* Layout selector */}
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--legatum-text-muted)] font-bold">
          Módulos
        </div>
        <div
          className="inline-flex rounded-lg border border-[var(--legatum-border)] bg-[var(--legatum-surface)] p-1 text-xs font-semibold"
          data-testid="dashboard-layout-toggle"
        >
          <button
            onClick={() => setLayout("row")}
            data-testid="layout-row"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-colors ${
              layout === "row"
                ? "bg-[var(--legatum-accent)] text-[var(--legatum-accent-fg)]"
                : "text-[var(--legatum-text-secondary)] hover:text-[var(--legatum-text)]"
            }`}
          >
            <Rows3 className="h-3.5 w-3.5" /> Vertical
          </button>
          <button
            onClick={() => setLayout("bento")}
            data-testid="layout-bento"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-colors ${
              layout === "bento"
                ? "bg-[var(--legatum-accent)] text-[var(--legatum-accent-fg)]"
                : "text-[var(--legatum-text-secondary)] hover:text-[var(--legatum-text)]"
            }`}
          >
            <LayoutGrid className="h-3.5 w-3.5" /> Mosaico
          </button>
        </div>
      </div>

      {/* Modules — agrupados por categoría (Iter 96). Mantiene el toggle
          Vertical/Mosaico existente: dentro de cada categoría se aplica el
          layout seleccionado. */}
      {(() => {
        const byCat = modulesByCategory();
        const isBento = layout === "bento";
        return (
          <div className="space-y-8" data-testid="dashboard-modules-grouped">
            {CATEGORIES.map((cat) => {
              const mods = (byCat[cat.key] || []).filter((m) =>
                isAdminRole ||
                !Array.isArray(allowedMods) || allowedMods.length === 0 || allowedMods.includes(m.key) ||
                // Compatibilidad RBAC legacy: si el usuario tiene 'avales' o
                // 'garantias' permitidos, ve también el módulo unificado.
                (m.key === "avales_garantias" && (allowedMods.includes("avales") || allowedMods.includes("garantias"))),
              );
              if (mods.length === 0) return null;
              const Icon = cat.Icon;
              return (
                <section key={cat.key} data-testid={`dashboard-cat-${cat.key}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                      style={{ background: cat.color + "22", color: cat.color }}
                    >
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase font-bold tracking-[0.22em]" style={{ color: cat.color }}>
                        {cat.label}
                      </div>
                      <div className="text-xs text-[var(--legatum-text-muted)]">{cat.desc}</div>
                    </div>
                    <span className="text-[10px] font-mono text-[var(--legatum-text-muted)]">{mods.length}</span>
                  </div>
                  {isBento ? (
                    <div className="grid grid-cols-1 md:grid-cols-4 grid-flow-row-dense gap-4 md:gap-5 md:auto-rows-[minmax(210px,auto)]">
                      {mods.map((m, i) => (
                        <ModuleCard key={m.key} module={m} index={i} stat={statForModule(m.key)} layout="bento" />
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-3">
                      {mods.map((m, i) => (
                        <ModuleCard key={m.key} module={m} index={i} stat={statForModule(m.key)} layout="row" />
                      ))}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        );
      })()}
      </Layout>
    </>
  );
}


function LegalDeadlineBanner() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/correo/badge-count")
      .then((r) => alive && setData(r.data))
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!data || data.total === 0) return null;
  const p = data.proximo_plazo;
  const urgente = p && p.dias_restantes <= 3;
  const color = urgente ? "#DC2626" : data.total > 0 ? "#E08A1F" : "#0F8A5F";
  const bg = urgente ? "rgba(220,38,38,0.08)" : "rgba(224,138,31,0.06)";
  return (
    <div
      className="mt-4 rounded-2xl border-2 p-4 flex items-center gap-4 cursor-pointer hover:shadow-lg transition-shadow"
      style={{ borderColor: color, background: bg }}
      onClick={() => navigate("/correo")}
      data-testid="dashboard-legal-banner"
    >
      <div className="text-3xl" style={{ color }}>{urgente ? "🚨" : "⚠️"}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-extrabold" style={{ color }}>
          {data.total} asunto{data.total === 1 ? "" : "s"} legal{data.total === 1 ? "" : "es"} requier{data.total === 1 ? "e" : "en"} tu atención
        </div>
        <div className="text-[11px] text-[var(--legatum-text-muted)]">
          {data.emails_pendientes > 0 && <span>{data.emails_pendientes} email{data.emails_pendientes === 1 ? "" : "s"} sin procesar · </span>}
          {data.plazos_criticos > 0 && <span>{data.plazos_criticos} plazo{data.plazos_criticos === 1 ? "" : "s"} crítico{data.plazos_criticos === 1 ? "" : "s"} · </span>}
          {data.kratos_pendientes > 0 && <span>{data.kratos_pendientes} notificación{data.kratos_pendientes === 1 ? "" : "es"} Kratos · </span>}
        </div>
        {p && (
          <div className="text-[11px] mt-1" style={{ color }}>
            <b>Más urgente:</b> {p.expediente_numero ? `${p.expediente_numero} · ` : ""}{p.asunto} — vence {" "}
            <b>{p.dias_restantes === 0 ? "HOY" : p.dias_restantes === 1 ? "mañana" : `en ${p.dias_restantes} días`}</b>
            {" "}({p.fecha_limite})
          </div>
        )}
      </div>
      <div className="text-xs font-bold uppercase tracking-wider" style={{ color }}>
        Ir a Correo →
      </div>
    </div>
  );
}
